import { createContext, useCallback, useContext, useEffect, useMemo, useState, Fragment } from "react";
import { resolveLanguage, STORAGE_KEY, DEFAULT_LANGUAGE } from "src/i18n/resolveLanguage";
import { interpolate, splitRich } from "src/i18n/interpolate";
import { fi } from "src/i18n/fi";
import { en } from "src/i18n/en";

const DEFAULT_BUNDLES = { fi, en };
const LanguageContext = createContext(null);

/**
 * Resolve a dotted key against a bundle. Returns undefined (not a throw) when
 * the path is missing or lands on a non-string or an empty string, so callers'
 * `??` fallback chains reach the Finnish bundle / raw key (fb#1272).
 */
export function lookup(bundle, key) {
    const value = String(key).split(".").reduce((node, part) => (node == null ? undefined : node[part]), bundle);
    return typeof value === "string" && value !== "" ? value : undefined;
}

export function LanguageProvider({ children, initialLanguage, bundles = DEFAULT_BUNDLES }) {
    const [lang, setLangState] = useState(() => {
        if (initialLanguage) return initialLanguage;
        let stored;
        try { stored = window.localStorage.getItem(STORAGE_KEY); } catch { stored = null; }
        return resolveLanguage({ search: window.location.search, stored });
    });

    // Screen readers pick pronunciation from this; index.html hardcodes "fi".
    useEffect(() => { document.documentElement.lang = lang; }, [lang]);

    const setLang = useCallback((next) => {
        setLangState(next);
        try { window.localStorage.setItem(STORAGE_KEY, next); } catch { /* private mode — in-memory only */ }
    }, []);

    // Message resolution: active bundle → Finnish bundle → the key itself. The
    // Finnish step means an un-translated key degrades to real Finnish copy
    // rather than a raw dotted key in front of a user.
    const resolve = useCallback(
        (key) => lookup(bundles[lang], key) ?? lookup(bundles[DEFAULT_LANGUAGE], key) ?? String(key),
        [bundles, lang]
    );

    const value = useMemo(() => ({ lang, setLang, resolve }), [lang, setLang, resolve]);
    return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

function useLanguageContext() {
    const ctx = useContext(LanguageContext);
    if (!ctx) throw new Error("useT/useLang used outside <LanguageProvider>");
    return ctx;
}

export function useLang() {
    const { lang, setLang } = useLanguageContext();
    return { lang, setLang };
}

/** t("wizard.step3.title") · t("quote.total", { n: 12 }) */
export function useT() {
    const { resolve } = useLanguageContext();
    // vars ?? undefined: interpolate.js defaults `vars = {}` only on undefined,
    // so an explicit t(key, null) would otherwise throw inside its
    // hasOwnProperty.call(null, ...) check. Guarded here rather than in
    // interpolate.js (Task 2, already reviewed/committed).
    return useCallback((key, vars) => interpolate(resolve(key), vars ?? undefined), [resolve]);
}

/** tRich("consent.line", { link: <Link to="/kayttoehdot">…</Link> }) */
export function useTRich() {
    const { resolve } = useLanguageContext();
    // slots ?? {}: same reasoning as useT's `vars ?? undefined` above — a
    // default parameter only fires on undefined, so an explicit
    // tRich(key, null) would otherwise throw inside Object.keys(null).
    return useCallback(
        (key, slots) => {
            const s = slots ?? {};
            return splitRich(resolve(key), Object.keys(s)).map((part, i) => (
                <Fragment key={i}>{part.type === "slot" ? s[part.value] : part.value}</Fragment>
            ));
        },
        [resolve]
    );
}
